import React, { useEffect, useMemo, useState } from "react";
import { getSentHistory, updateSent, deleteSent, generateFollowup, sendFollowup } from "../lib/api";
import { useCountry } from "../lib/countryContext";
import { useSenderProfile } from "../lib/senderProfile";
import { SectionTitle, StatBlock, EmptyState } from "../components/UIKit";
import { Envelope, MagnifyingGlass, ArrowClockwise, Trash, Sparkle, PaperPlaneTilt, X, EnvelopeOpen, Warning, CheckCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const RESPONSE_META = {
  pending: { label: "PENDING", color: "bg-white border-black/25 text-[#0A0A0A]", icon: Clock },
  interested: { label: "INTERESTED", color: "bg-[#D4FF00] border-black text-[#0A0A0A]", icon: CheckCircle },
  not_interested: { label: "NOT INTERESTED", color: "bg-[#F4F4F2] border-black/40 text-[#525252]", icon: X },
  booked: { label: "BOOKED", color: "bg-[#0047FF] border-[#0047FF] text-white", icon: CheckCircle },
  bounced: { label: "BOUNCED", color: "bg-red-100 border-red-600 text-red-800", icon: Warning },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.substring(0, 16);
  }
}

function daysSince(iso) {
  if (!iso) return 0;
  try {
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24)));
  } catch { return 0; }
}

function extractSubject(text) {
  if (!text) return "";
  const m = text.match(/^\s*(?:Betreff|Subject|Objet|Asunto|Oggetto|Onderwerp|Emne|Ämne|Aihe|Θέμα|Temat|Assunto|Předmět|Tárgy|Temă)\s*:\s*(.+)$/mi);
  return m ? m[1].trim() : "";
}
function extractBody(text) {
  if (!text) return "";
  return text.replace(/^\s*(?:Betreff|Subject|Objet|Asunto|Oggetto|Onderwerp|Emne|Ämne|Aihe|Θέμα|Temat|Assunto|Předmět|Tárgy|Temă)\s*:.+\n+/mi, "").trim();
}

export default function SentHistoryPage() {
  const { selected, countries } = useCountry();
  const [profile] = useSenderProfile();
  const [data, setData] = useState({ items: [], stats: {}, sent_today: 0, daily_cap: 40 });
  const [loading, setLoading] = useState(true);
  const [countryFilter, setCountryFilter] = useState("all"); // "all" | code
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [editingNote, setEditingNote] = useState({}); // id -> text
  const [savingNote, setSavingNote] = useState(null);
  const [openRow, setOpenRow] = useState(null); // sent_id whose body is open

  // Follow-up modal state
  const [followup, setFollowup] = useState(null); // {sent_id, email, preview, subject, recipient_email, recipient_name, days_since, original_subject, language}
  const [followupGenerating, setFollowupGenerating] = useState(false);
  const [followupSending, setFollowupSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (countryFilter !== "all") params.country_code = countryFilter;
      if (statusFilter !== "all") params.response_status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const res = await getSentHistory(params);
      setData(res);
    } catch (e) {
      toast.error("Failed to load sent history");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [countryFilter, statusFilter]);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const setStatus = async (id, next) => {
    try {
      await updateSent(id, { response_status: next });
      setData((d) => ({
        ...d,
        items: d.items.map((it) => (it.id === id ? { ...it, response_status: next } : it)),
      }));
      // Refresh funnel stats
      const params = {};
      if (countryFilter !== "all") params.country_code = countryFilter;
      if (statusFilter !== "all") params.response_status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const res = await getSentHistory(params);
      setData(res);
      toast.success(`Marked as ${RESPONSE_META[next]?.label.toLowerCase() || next}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const saveNote = async (id) => {
    const text = editingNote[id] ?? "";
    setSavingNote(id);
    try {
      await updateSent(id, { notes: text });
      setData((d) => ({
        ...d,
        items: d.items.map((it) => (it.id === id ? { ...it, notes: text } : it)),
      }));
      setEditingNote((m) => { const c = { ...m }; delete c[id]; return c; });
      toast.success("Note saved");
    } catch {
      toast.error("Save failed");
    }
    setSavingNote(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record? Won't recall the email — just removes it from history.")) return;
    try {
      await deleteSent(id);
      setData((d) => ({ ...d, items: d.items.filter((it) => it.id !== id) }));
      toast.success("Removed from history");
    } catch { toast.error("Delete failed"); }
  };

  const openFollowup = async (row) => {
    setFollowupGenerating(true);
    setFollowup({ sent_id: row.id, generating: true, recipient_name: row.recipient_name, recipient_email: row.recipient_email, original_subject: row.subject });
    try {
      const res = await generateFollowup({ sent_id: row.id, tone: "friendly", sender: profile });
      setFollowup({
        sent_id: row.id,
        email: res.email,
        preview: res.preview,
        language: res.language,
        recipient_email: res.recipient_email,
        recipient_name: res.recipient_name,
        days_since: res.days_since,
        original_subject: res.original_subject,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Follow-up generation failed");
      setFollowup(null);
    }
    setFollowupGenerating(false);
  };

  const submitFollowup = async () => {
    if (!followup) return;
    const subject = extractSubject(followup.email) || `Re: ${followup.original_subject || ""}`;
    const body = extractBody(followup.email);
    if (!body) {
      toast.error("Empty follow-up — regenerate first");
      return;
    }
    setFollowupSending(true);
    try {
      await sendFollowup({ sent_id: followup.sent_id, subject, body });
      toast.success(`Follow-up sent to ${followup.recipient_name || followup.recipient_email}`);
      setFollowup(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setFollowupSending(false);
  };

  const items = data.items || [];
  const stats = data.stats || {};
  const responseRate = stats.total ? Math.round(((stats.interested || 0) + (stats.booked || 0)) / stats.total * 100) : 0;

  const activeCountries = useMemo(() => {
    const s = new Set(items.map((i) => i.country_code).filter(Boolean));
    return countries.filter((c) => s.has(c.code));
  }, [items, countries]);

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle overline="OUTREACH · TIMELINE" title="Sent History & Replies" />

      {/* Funnel stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="history-stats">
        <StatBlock label="Total Sent" value={stats.total ?? "—"} hint="All time" testId="stat-total" />
        <StatBlock label="Pending" value={stats.pending ?? "—"} hint="No response yet" testId="stat-pending" />
        <StatBlock label="Interested" value={stats.interested ?? "—"} hint={`${responseRate}% response rate`} testId="stat-interested" accent />
        <StatBlock label="Booked" value={stats.booked ?? "—"} hint="Calls / deals" testId="stat-booked" />
        <StatBlock label="Not Interested" value={stats.not_interested ?? "—"} hint="Closed / no thanks" testId="stat-not-interested" />
      </div>

      {/* Filters */}
      <div className="border border-black bg-white p-3 mb-6 flex flex-wrap items-center gap-3" data-testid="history-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} weight="bold" className="text-[#525252]" />
          <input
            data-testid="history-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search recipient, subject or notes…"
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          />
        </div>
        <select
          data-testid="history-country-filter"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="border border-black/25 px-2 py-1.5 text-xs bg-white"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          <option value="all">All countries</option>
          {activeCountries.map((c) => (
            <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
          ))}
        </select>
        <select
          data-testid="history-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-black/25 px-2 py-1.5 text-xs bg-white"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          <option value="all">Any status</option>
          <option value="pending">Pending</option>
          <option value="interested">Interested</option>
          <option value="booked">Booked</option>
          <option value="not_interested">Not interested</option>
          <option value="bounced">Bounced</option>
        </select>
        <button
          data-testid="history-refresh"
          onClick={load}
          className="border border-black bg-white px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-black/5 flex items-center gap-1.5"
          style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
        >
          <ArrowClockwise size={12} weight="bold" /> REFRESH
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-[#525252]" data-testid="history-loading">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Envelope size={22} weight="bold" />}
          title="Nothing sent yet"
          hint="Once you send a pitch from Outreach, it lands here so you can track replies and follow up."
        />
      ) : (
        <div className="border border-black bg-white overflow-x-auto" data-testid="history-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black text-left bg-[#F4F4F2]">
                {["When", "Recipient", "Subject", "Response", "Notes", "Actions"].map((h) => (
                  <th key={h} className="py-2 px-3 text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const status = row.response_status || "pending";
                const meta = RESPONSE_META[status] || RESPONSE_META.pending;
                const StatusIcon = meta.icon;
                const days = daysSince(row.sent_at);
                const canFollowup = status === "pending" && !row.followup_sent_at && days >= 3 && row.recipient_email;
                const country = countries.find((c) => c.code === row.country_code);
                const noteVal = editingNote[row.id] ?? (row.notes || "");
                const noteDirty = editingNote[row.id] !== undefined && editingNote[row.id] !== (row.notes || "");
                return (
                  <React.Fragment key={row.id}>
                    <tr className={`border-b border-black/10 last:border-b-0 ${row.is_followup ? "bg-[#F4F4F2]/60" : ""}`} data-testid={`history-row-${row.id}`}>
                      <td className="py-2 px-3 text-xs whitespace-nowrap" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {fmtDate(row.sent_at)}
                        <div className="text-[10px] text-[#525252]">
                          {days === 0 ? "today" : `${days}d ago`}
                          {row.is_followup && <span className="ml-1 text-[#0047FF]">· follow-up</span>}
                          {row.followup_sent_at && <span className="ml-1 text-[#525252]">· followed up</span>}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="font-medium truncate max-w-[220px]" title={row.recipient_name}>
                          {country?.flag && <span className="mr-1">{country.flag}</span>}
                          {row.recipient_name || "—"}
                        </div>
                        <div className="text-[10px] text-[#525252] truncate max-w-[220px]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                          {row.recipient_email || "no email"}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-xs max-w-[240px]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        <button
                          onClick={() => setOpenRow(openRow === row.id ? null : row.id)}
                          className="text-left hover:underline truncate block w-full"
                          data-testid={`history-toggle-body-${row.id}`}
                          title={row.subject}
                        >
                          {row.subject || "(no subject)"}
                        </button>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase ${meta.color}`} style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                            <StatusIcon size={9} weight="bold" /> {meta.label}
                          </span>
                        </div>
                        <select
                          data-testid={`history-status-${row.id}`}
                          value={status}
                          onChange={(e) => setStatus(row.id, e.target.value)}
                          className="mt-1 text-[10px] border border-black/25 px-1 py-0.5 bg-white"
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                        >
                          <option value="pending">pending</option>
                          <option value="interested">interested</option>
                          <option value="booked">booked</option>
                          <option value="not_interested">not_interested</option>
                          <option value="bounced">bounced</option>
                        </select>
                      </td>
                      <td className="py-2 px-3 max-w-[240px]">
                        <textarea
                          data-testid={`history-note-${row.id}`}
                          value={noteVal}
                          onChange={(e) => setEditingNote((m) => ({ ...m, [row.id]: e.target.value }))}
                          placeholder="Add a note…"
                          rows={2}
                          className="w-full text-xs border border-black/15 px-2 py-1 outline-none focus:border-black bg-white resize-y"
                          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                        />
                        {noteDirty && (
                          <button
                            data-testid={`history-note-save-${row.id}`}
                            onClick={() => saveNote(row.id)}
                            disabled={savingNote === row.id}
                            className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#0047FF] hover:underline"
                            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                          >
                            {savingNote === row.id ? "SAVING…" : "SAVE NOTE"}
                          </button>
                        )}
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {canFollowup && (
                            <button
                              data-testid={`history-followup-${row.id}`}
                              onClick={() => openFollowup(row)}
                              className="bg-[#0047FF] text-white px-2 py-1 text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 hover:bg-[#0038CC]"
                              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                            >
                              <Sparkle size={10} weight="bold" /> FOLLOW-UP
                            </button>
                          )}
                          {!canFollowup && status === "pending" && days < 3 && row.recipient_email && (
                            <span className="text-[9px] text-[#525252]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                              wait {3 - days}d
                            </span>
                          )}
                          <button
                            data-testid={`history-delete-${row.id}`}
                            onClick={() => remove(row.id)}
                            className="border border-black/25 px-2 py-1 text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1 hover:bg-black/5"
                            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                            title="Remove from history"
                          >
                            <Trash size={10} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {openRow === row.id && (
                      <tr className="bg-[#F4F4F2]" data-testid={`history-body-${row.id}`}>
                        <td colSpan={6} className="p-4">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#525252] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                            EMAIL PREVIEW · {row.language || "—"}
                          </div>
                          <div className="text-sm whitespace-pre-wrap bg-white border border-black/15 p-3 max-h-72 overflow-y-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                            <div className="font-semibold mb-2">{row.subject}</div>
                            {row.body_preview || "(no preview stored)"}
                          </div>
                          {row.error && (
                            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                              ERROR: {row.error}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Follow-up modal */}
      {followup && (
        <div
          data-testid="followup-modal-overlay"
          onClick={() => !followupSending && setFollowup(null)}
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        >
          <div
            data-testid="followup-modal"
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-2 border-black shadow-[6px_6px_0px_#0047FF] max-w-2xl w-full max-h-[85vh] overflow-y-auto"
          >
            <div className="p-4 border-b border-black flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#0047FF] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  FOLLOW-UP · {followup.days_since || "?"} DAYS AFTER ORIGINAL
                </div>
                <div className="text-lg tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                  {followup.recipient_name || followup.recipient_email}
                </div>
                <div className="text-xs text-[#525252]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  original: {followup.original_subject || "—"}
                </div>
              </div>
              <button
                data-testid="followup-close"
                onClick={() => setFollowup(null)}
                disabled={followupSending}
                className="text-[#525252] hover:text-black"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {followupGenerating ? (
              <div className="p-6 text-sm text-[#525252]">
                Drafting a short follow-up in {followup.language || "the recipient's language"}…
              </div>
            ) : (
              <>
                <div className="p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#525252] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    DRAFT {followup.language && `· ${followup.language.toUpperCase()}`}
                  </div>
                  <textarea
                    data-testid="followup-textarea"
                    value={followup.email || ""}
                    onChange={(e) => setFollowup((f) => ({ ...f, email: e.target.value }))}
                    className="w-full min-h-[220px] p-3 text-sm border border-black/25 outline-none focus:border-black bg-white resize-y"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  />
                  {followup.preview && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                        English preview (won't be sent)
                      </summary>
                      <div className="mt-2 text-sm whitespace-pre-wrap bg-[#F4F4F2] border border-black/10 p-3" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        {followup.preview}
                      </div>
                    </details>
                  )}
                </div>
                <div className="p-4 border-t border-black flex flex-wrap gap-2 justify-end">
                  <button
                    data-testid="followup-regenerate"
                    onClick={() => openFollowup({ id: followup.sent_id, recipient_name: followup.recipient_name, recipient_email: followup.recipient_email, subject: followup.original_subject })}
                    disabled={followupGenerating || followupSending}
                    className="border border-black bg-white px-3 py-2 text-[10px] tracking-[0.2em] uppercase hover:bg-black/5 inline-flex items-center gap-1.5"
                    style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                  >
                    <ArrowClockwise size={12} weight="bold" /> REGENERATE
                  </button>
                  <button
                    data-testid="followup-send"
                    onClick={submitFollowup}
                    disabled={followupSending || !followup.email || !followup.recipient_email}
                    className="bg-[#0047FF] text-white px-3 py-2 text-[10px] tracking-[0.2em] uppercase shadow-[2px_2px_0px_#0A0A0A] hover:bg-[#0038CC] disabled:bg-[#525252] disabled:shadow-none inline-flex items-center gap-1.5"
                    style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                  >
                    <PaperPlaneTilt size={12} weight="bold" /> {followupSending ? "SENDING…" : "SEND FOLLOW-UP"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
